import { useState, useEffect, useRef, useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Typography, Stack, Paper, CircularProgress, Alert,
} from '@mui/material';
import { Close as CloseIcon, EditOutlined as EditIcon, Description as DocIcon } from '@mui/icons-material';
import { useAlert } from '../functions/useAlert';
import { DataContext } from '../contexts/DataProvider';
import { updateAgreement } from '../services/agreements.service';
import type { Agreement } from '../hooks/useAgreements';

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

interface AgreementEditDialogProps {
  open: boolean;
  agreement: Agreement | null;
  onClose: () => void;
  onUpdated: () => void;
}

const AgreementEditDialog = ({ open, agreement, onClose, onUpdated }: AgreementEditDialogProps) => {
  const [setAlert] = useAlert();
  const { login }: any = useContext(DataContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => { if (!open) setSelectedFile(null); }, [open]);

  const handleSave = async () => {
    if (!agreement || !selectedFile) return;
    setIsSaving(true);
    try {
      const payload = new FormData();
      payload.append('file', selectedFile);
      payload.append('orgId', login.data.orgId);
      payload.append('userId', login.data.id);
      const resp: any = await updateAgreement(agreement._id, payload);
      if (resp?.hasError) throw new Error(resp.message);
      setAlert(resp?.message || 'Agreement updated successfully!', 'success');
      onUpdated();
      onClose();
    } catch (err: any) {
      setAlert(err.message || 'Failed to update agreement.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '12px' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack>
          <Typography variant="h6" fontWeight={700}>Edit Agreement</Typography>
          {agreement && <Typography variant="caption" color="text.secondary">Replace: {agreement.fileName}</Typography>}
        </Stack>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Upload a revised PDF to replace the existing file. Field placements will need to be reconfigured after replacement.
          </Alert>
          <input
            ref={fileInputRef} type="file" accept=".pdf,application/pdf"
            style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files?.[0]) setSelectedFile(e.target.files[0]); }}
          />
          <Paper
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            sx={{
              p: 3, textAlign: 'center', cursor: 'pointer', borderRadius: 2,
              borderStyle: 'dashed',
              borderColor: selectedFile ? 'primary.main' : 'divider',
              bgcolor: selectedFile ? 'primary.50' : 'background.default',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <DocIcon sx={{ fontSize: 40, color: selectedFile ? 'primary.main' : 'text.secondary' }} />
            <Typography variant="body2" mt={1} color={selectedFile ? 'primary.main' : 'text.secondary'} fontWeight={600}>
              {selectedFile ? selectedFile.name : 'Click to browse .pdf file'}
            </Typography>
            {selectedFile && <Typography variant="caption" color="text.secondary">{formatFileSize(selectedFile.size)}</Typography>}
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">Cancel</Button>
        <Button
          onClick={handleSave} variant="contained" disabled={!selectedFile || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
        >
          {isSaving ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgreementEditDialog;
